import { Controller } from "@hotwired/stimulus";

/**
 * Pricing Controller - Price display & recurrence management
 *
 * Responsibilities:
 * • Monthly/yearly recurrence switching & display
 * • Viewer dropdown pricing updates (premium plans)
 * • Price display from backend data
 * • Currency formatting integration with CurrencyManage.js
 * • Inter-controller communication via plan-changed events
 *
 * Replaces jQuery:
 * • CurrencyManage.updateCurrencyElements()
 * • Plan.updateViewerCount() pricing logic
 * • Monthly/yearly toggle functionality
 */

export default class extends Controller {
  static REC_MONTH = "month";
  static REC_YEAR = "year";
  static NON_RECURRING_ADDONS = ["onboarding", "onboarding_offline"];
  static ADDON_MODAL_SELECTOR = "#pricing-addon-modal";

  static DEFAULTS = {
    DEFAULT_RECURRENCE: "year"
  };

  static targets = [
    "monthlyPrice",
    "yearlyPrice",
    "monthlyAmount",
    "yearlyAmount",
    "savingsAmount",
    "billingText",
    "billingAmount",
    "viewerSelect",
    "planCard", // Generic target for all plan cards by category
    "badgesList",
    "annualComparison",
    "annualTotal"
  ];

  static values = {
    selectedPlan: String,
    recurrence: String,
    currentRecurrence: String,
    currentPlan: String,
    nonRecurringAddons: Array,
    initialAddonsByCategory: Object
  };

  connect() {
    this._requestId = 0;
    this.recurrenceValue ||= this.currentRecurrenceValue || this.constructor.DEFAULTS.DEFAULT_RECURRENCE;

    this.toggleRecurrenceDisplay();
    this.initializeViewerCount();
    this.bindEvents();
    this.updateAnnualBillingNotes();
    this.updateCurrencyDisplay();
    this.addonPricesByCategory = {};
    this.addonSelectionsByCategory = {};
    this.initializeAddonState();

    // Refresh prices if any addons are pre-selected (server-rendered prices don't include addons)
    if (this.hasSelectedAddons()) {
      const requestId = this.nextRequest();
      this.refreshAllPlanPrices(requestId);
    }
  }

  disconnect() {
    this.unbindEvents();
  }

  bindEvents() {
    this.element.addEventListener("plans:plan-changed", this.handlePlanChanged);
    this.element.addEventListener("plans:recurrence-changed", this.handleRecurrence);
    this.element.addEventListener("addon:selection-changed", this.handleAddonSelectionChanged);

    // We need to listen to currency dropdown changes globally since the dropdown lives
    // outside this controller's scope. We may refactor the currency selector to a stimulus controller in future
    $(document).on("click", ".currency-dropdown a[data-curr]", this.handleCurrencyChange);

    if (window.CurrencyManage) {
      $(window.CurrencyManage).on("change", this.handleCurrencyManageChange);
    }
  }

  unbindEvents() {
    this.element.removeEventListener("plans:plan-changed", this.handlePlanChanged);
    this.element.removeEventListener("plans:recurrence-changed", this.handleRecurrence);
    this.element.removeEventListener("addon:selection-changed", this.handleAddonSelectionChanged);
    $(document).off("click", ".currency-dropdown a[data-curr]", this.handleCurrencyChange);

    if (window.CurrencyManage) {
      $(window.CurrencyManage).off("change", this.handleCurrencyManageChange);
    }
  }

  // Events

  nextRequest() {
    return ++this._requestId;
  }

  isLatest(id) {
    return id === this._requestId;
  }

  handlePlanChanged = (event) => {
    if (event.detail?.planName) {
      this.onPlanChanged(event.detail.planName);
    } else {
      this.refreshPricingWithAddons();
    }
  };

  handleRecurrence = (event) => {
    if (event.detail?.recurrence) {
      this.onRecurrenceChanged(event.detail.recurrence);
    } else {
      this.recurrenceValueChanged();
    }
  };

  handleAddonSelectionChanged = (event) => {
    const { category, key, value } = event.detail || {};

    if (!category) {
      this.refreshPricingWithAddons();
      return;
    }

    // Update state directly from event data (no DOM reading)
    if (key !== undefined) {
      this.updateAddonInState(category, key, value);
    }

    this.refreshPricingForCategory(category);
  };

  // Wait for next animation frame to ensure dropdown has updated the display
  handleCurrencyChange = () => requestAnimationFrame(() => this.updateCurrencyDisplay());

  // Inter-controller communication

  onPlanChanged(planName) {
    this.selectedPlanValue = this.normalizePlanName(planName);
    this.refreshPricingWithAddons();
  }

  onRecurrenceChanged(recurrence) {
    this.recurrenceValue = recurrence;
    this.recurrenceValueChanged();
  }

  normalizePlanName(planName) {
    return this.viewerSelectTarget?.selectedOptions?.[0]?.dataset?.plan || planName;
  }

  handleViewerSelection() {
    const selectedOption = this.viewerSelectTarget?.selectedOptions?.[0];
    if (!selectedOption) return;

    const { plan } = selectedOption.dataset;
    if (!plan) return;

    this.selectedPlanValue = plan;

    // Find the card containing the viewer dropdown to get its category
    const card = this.viewerSelectTarget.closest("[data-category]");
    const category = card?.dataset.category;

    // Notify plans controller
    this.dispatch("plan-changed", { detail: { planName: plan, category: category }, bubbles: true });

    if (category) {
      this.refreshPricingForCategory(category);
    } else {
      this.refreshPricingWithAddons();
    }
  }

  initializeViewerCount() {
    if (!this.hasViewerSelectTarget || !this.currentPlanValue) return;
    const option = this.viewerSelectTarget.querySelector(`option[data-plan="${this.currentPlanValue}"]`);
    if (option) this.viewerSelectTarget.value = option.value;
  }

  // Display updates

  updateField(targets, card, euroValue) {
    const el = targets.find(t => card.contains(t));
    if (!el) return;

    // Update data-euro attribute with EUR value.
    // CurrencyManage.updateCurrencyElements() will read this and convert/display in selected currency.
    el.dataset.euro = euroValue;

    const inner = el.querySelector("span.amount[data-euro]");
    if (inner) {
      inner.dataset.euro = euroValue;
    }
  }

  toggleRecurrenceDisplay() {
    const isMonthly = this.recurrenceValue === this.constructor.REC_MONTH;

    this.monthlyPriceTargets.forEach(price => price.classList.toggle("hidden", !isMonthly));
    this.yearlyPriceTargets.forEach(price => price.classList.toggle("hidden", isMonthly));

    this.billingTextTargets.forEach(text => {
      text.classList.toggle("billed-per-month", isMonthly);
      text.classList.toggle("billed-per-year", !isMonthly);
    });
  }

  async refreshAllPlanPrices(requestId = null) {
    // Fetch pricing for all plan cards. If adding dropdowns to multiple categories,
    // associate each dropdown with its card and deduplicate requests by (planName, recurrence).
    const fetchPromises = [];

    this.planCardTargets.forEach(card => {
      const category = card.dataset.category;
      if (!category) return;

      const planName = this.getPlanNameForCategory(category);
      if (!planName) return;

      const addons = this.getAddonsForPlan(category);
      fetchPromises.push(this.fetchBackendPricing(planName, this.recurrenceValue, requestId, addons));
    });

    await Promise.all(fetchPromises);
  }

  async refreshPricingForCategory(category) {
    if (!category) return;

    const planName = this.getPlanNameForCategory(category);
    if (!planName) return;

    const addons = this.getAddonsForPlan(category);
    const requestId = this.nextRequest();
    await this.fetchBackendPricing(planName, this.recurrenceValue, requestId, addons);
  }

  async fetchBackendPricing(planName, recurrence, requestId = null, addons = {}) {
    try {
      const recurring = {};
      const once = {};
      const nonRecurringAddons = this.nonRecurringAddonsValue || [];

      Object.entries(addons).forEach(([key, value]) => {
        if (nonRecurringAddons.includes(key)) {
          once[key] = value;
        } else {
          recurring[key] = value;
        }
      });

      const response = await fetch("/admin/plan_previews", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({
          plan: {
            plan_type_name: planName,
            recurrence,
            ...(Object.keys(recurring).length && { features: recurring }),
            ...(Object.keys(once).length && { once })
          }
        })
      });

      if (!response.ok) return;

      const json = await response.json();

      // Prevent race conditions from rapid toggling
      if (requestId && !this.isLatest(requestId)) return;

      this.applyBackendPricing(json);
    } catch (e) {
      console.error("Error fetching backend plan preview:", e);
    }
  }

  applyBackendPricing(data) {
    if (!data?.amounts || data.amounts.per_month == null) return;

    const { per_month, billed_per_period, savings = 0 } = data.amounts;
    const { category, recurrence, available_addons } = data;

    this.updateDisplayedPrices(category, recurrence, per_month, billed_per_period, savings);

    if (available_addons) {
      this.updateAddonPrices(category, available_addons);
    }
  }

  updateAddonPrices(category, addonPrices) {
    this.addonPricesByCategory[category] = addonPrices;

    const badgesList = this.badgesListTargets.find(list =>
      list.dataset.badgePlanType === category
    );

    Object.entries(addonPrices).forEach(([addonKey, priceData]) => {
      const { display_price } = priceData;
      const displayPrice = display_price || priceData;

      this.updateBadgePrices(badgesList, addonKey, displayPrice);
      if (this.shouldUpdateModalPricesFor(category)) {
        this.updateModalPrices(addonKey, priceData);
      }
    });

    this.updateAnnualBillingNotes();
    this.updateCurrencyDisplay();
  }

  updateBadgePrices(badgesList, addonKey, displayPrice) {
    if (!badgesList) return;

    badgesList.querySelectorAll(`[data-badge-key="${addonKey}"] [data-euro]`).forEach(el => {
      el.dataset.euro = displayPrice;
    });
  }

  updateModalPrices(addonKey, priceData) {
    const modal = document.querySelector(this.constructor.ADDON_MODAL_SELECTOR);
    if (!modal) return;

    const addonRow = modal.querySelector(`[data-addon="${addonKey}"]`);
    if (!addonRow) return;

    const displayPrice = priceData.display_price || priceData;

    // Update main display price
    addonRow.querySelectorAll(".price [data-euro]").forEach(el => {
      el.dataset.euro = displayPrice;
    });

    // Update annual comparison note (shows monthly equivalent of yearly plan)
    if (priceData.year_price_per_month != null) {
      const annualComparisonEl = addonRow.querySelector("[data-pricing-target='annualComparison'] [data-euro]");
      if (annualComparisonEl) {
        annualComparisonEl.dataset.euro = priceData.year_price_per_month;
      }
    }

    // Update annual total if available
    if (priceData.year_price_total != null) {
      const annualTotalEl = addonRow.querySelector("[data-pricing-target='annualTotal'] [data-euro]");
      if (annualTotalEl) {
        annualTotalEl.dataset.euro = priceData.year_price_total;
      }
    }
  }

  shouldUpdateModalPricesFor(category) {
    const modal = document.querySelector(this.constructor.ADDON_MODAL_SELECTOR);
    if (!modal) return false;

    const currentCategory = modal.dataset.currentPlanCategory;
    return currentCategory === category;
  }

  updateAnnualBillingNotes() {
    const isMonthly = this.recurrenceValue === this.constructor.REC_MONTH;

    // Toggle between monthly note (Annual plan: X/mo) and annual note (Annual total: X/yr)
    this.annualComparisonTargets.forEach(note => {
      note.style.display = isMonthly ? "" : "none";
    });

    this.annualTotalTargets.forEach(note => {
      note.style.display = isMonthly ? "none" : "";
    });
  }

  // Helper Methods

  findCardForPlan(category) {
    return this.planCardTargets.find(card => card.dataset.category === category);
  }

  capabilitiesFor(category) {
    const card = this.findCardForPlan(category);
    return JSON.parse(card?.dataset.capabilities || "{}");
  }

  getPlanNameForCategory(category) {
    const card = this.findCardForPlan(category);
    if (!card) return null;

    const capabilities = this.capabilitiesFor(category);

    if (capabilities.hasSelectableViewerLimit && this.hasViewerSelectTarget) {
      return this.viewerSelectTarget.selectedOptions?.[0]?.dataset?.plan ||
             this.viewerSelectTarget.options[0]?.dataset?.plan;
    }

    return card.dataset.plan;
  }

  updateDisplayedPrices(category, recurrence, perMonth, billedPerPeriod, savings) {
    const card = this.findCardForPlan(category);
    if (!card) return;

    if (recurrence === this.constructor.REC_MONTH) {
      this.updateField(this.monthlyAmountTargets, card, perMonth);
    } else {
      this.updateField(this.yearlyAmountTargets, card, perMonth);
      this.updateField(this.savingsAmountTargets, card, savings);
      this.updateField(this.billingAmountTargets, card, billedPerPeriod);
    }

    this.updateCurrencyDisplay();
  }

  updateCurrencyDisplay() {
    if (!window.CurrencyManage) return;

    CurrencyManage.updateCurrencyElements();
    this.stripTrailingZeros();
  }

  stripTrailingZeros() {
    // Use queueMicrotask to ensure this runs after CurrencyManage DOM updates but before paint
    queueMicrotask(() => {
      this.element.querySelectorAll("[data-euro][data-decimals='2']").forEach(el => {
        el.textContent = el.textContent.replace(/[.,]00$/, "");
      });
    });
  }

  handleCurrencyManageChange = () => {
    // CurrencyManage triggers this event after updating currency
    this.stripTrailingZeros();
  };

  async recurrenceValueChanged() {
    const requestId = this.nextRequest();
    await this.refreshAllPlanPrices(requestId);

    if (!this.isLatest(requestId)) return;
    this.toggleRecurrenceDisplay();
    this.updateAnnualBillingNotes();
  }

  async showAddonModal(event) {
    event.preventDefault();

    const planCategory = event.currentTarget.dataset.planCategory;
    if (planCategory) {
      const modal = document.querySelector(this.constructor.ADDON_MODAL_SELECTOR);
      if (modal) {
        modal.dataset.currentPlanCategory = planCategory;
      }
      this.filterModalAddonsByPlan(planCategory);

      // Populate modal inputs from stored state for this category
      this.populateModalFromCategoryState(planCategory);

      // Fetch fresh prices for this category before opening modal
      const planName = this.getPlanNameForCategory(planCategory);

      if (planName) {
        const addons = this.getAddonsForPlan(planCategory);
        const requestId = this.nextRequest();
        await this.fetchBackendPricing(planName, this.recurrenceValue, requestId, addons);

        // Only update modal if this is still the latest request
        if (!this.isLatest(requestId)) return;
      }

      // Update modal DOM with prices from the fetch above
      const addonPrices = this.addonPricesByCategory[planCategory];
      if (addonPrices) {
        Object.entries(addonPrices).forEach(([addonKey, priceData]) => {
          this.updateModalPrices(addonKey, priceData);
        });
      }

      this.updateCurrencyDisplay();
    }

    WGModal.open(this.constructor.ADDON_MODAL_SELECTOR);
  }

  filterModalAddonsByPlan(planCategory) {
    const modal = document.querySelector(this.constructor.ADDON_MODAL_SELECTOR);
    if (!modal) return;

    // Show/hide addon rows based on which categories they belong to
    modal.querySelectorAll("[data-addon-categories]").forEach(addonRow => {
      const categories = addonRow.dataset.addonCategories.split(",");
      const shouldShow = categories.includes(planCategory);

      addonRow.style.display = shouldShow ? "" : "none";
    });

    // Hide empty groups
    modal.querySelectorAll(".closeable-group").forEach(group => {
      const visibleAddons = Array.from(
        group.querySelectorAll("[data-addon-categories]")
      ).filter(addon => addon.style.display !== "none");

      group.style.display = visibleAddons.length > 0 ? "" : "none";
    });
  }

  // Addon Collection: Gather selected addons per category

  initializeAddonState() {
    // Server provides initial addon state for existing plans (trials get empty object)
    const serverAddons = this.initialAddonsByCategoryValue || {};

    Object.entries(serverAddons).forEach(([category, addons]) => {
      this.addonSelectionsByCategory[category] = { ...addons };
    });

    // Sync badge visuals with server-provided initial state
    this.syncBadgesWithInitialState(serverAddons);
  }

  syncBadgesWithInitialState(serverAddons) {
    Object.entries(serverAddons).forEach(([category, addons]) => {
      const badgesList = this.badgesListTargets.find(list =>
        list.dataset.badgePlanType === category
      );
      if (!badgesList) return;

      Object.entries(addons).forEach(([addonKey, quantity]) => {
        badgesList.querySelectorAll(`[data-badge-key="${addonKey}"]`).forEach(badge => {
          const shouldBeInactive = quantity <= 0;
          badge.classList.toggle("wg-chip-gray-outline", shouldBeInactive);
        });
      });
    });
  }

  hasSelectedAddons() {
    if (!this.addonSelectionsByCategory) return false;
    return Object.values(this.addonSelectionsByCategory).some(addons =>
      Object.keys(addons).length > 0
    );
  }

  getAddonsForPlan(category) {
    if (category && this.addonSelectionsByCategory && this.addonSelectionsByCategory[category]) {
      return { ...this.addonSelectionsByCategory[category] };
    }
    return {};
  }

  updateAddonInState(category, key, value) {
    if (!this.addonSelectionsByCategory) {
      this.addonSelectionsByCategory = {};
    }
    if (!this.addonSelectionsByCategory[category]) {
      this.addonSelectionsByCategory[category] = {};
    }

    if (value > 0) {
      this.addonSelectionsByCategory[category][key] = value;
    } else {
      delete this.addonSelectionsByCategory[category][key];
    }
  }

  inputBelongsToCategory(input, category) {
    const addonRow = input.closest("[data-addon-categories]");
    return addonRow && addonRow.dataset.addonCategories.split(",").includes(category);
  }

  populateModalFromCategoryState(category) {
    const modal = document.querySelector(this.constructor.ADDON_MODAL_SELECTOR);
    if (!modal || !category) return;

    const addons = this.addonSelectionsByCategory[category] || {};

    modal.querySelectorAll(".switch-wrapper input[type='checkbox']").forEach(checkbox => {
      if (!this.inputBelongsToCategory(checkbox, category)) return;

      checkbox.checked = !!addons[checkbox.name];

      // Sync closed card state
      const closedCard = modal.querySelector(`.group-closed-content .closed-card[data-setting="${checkbox.name}"]`);
      if (closedCard) {
        closedCard.classList.remove("switch-on", "switch-off");
        closedCard.classList.add(checkbox.checked ? "switch-on" : "switch-off");
      }

      const addonRow = checkbox.closest(".addon-option");
      if (addonRow) addonRow.classList.toggle("active", checkbox.checked);
    });

    modal.querySelectorAll(".quantity-input").forEach(input => {
      if (!this.inputBelongsToCategory(input, category)) return;

      input.value = addons[input.name] || 0;

      // Sync closed card quantity display
      const targetSpan = modal.querySelector(`#addon-${input.name}`);
      if (targetSpan) targetSpan.textContent = input.value || "0";

      const addonRow = input.closest(".addon-option");
      if (addonRow) addonRow.classList.toggle("active", (parseInt(input.value, 10) || 0) > 0);
    });
  }

  splitRecurringAndOneTimeAddons(addons) {
    const recurring = {};
    const once = {};

    Object.entries(addons || {}).forEach(([key, quantity]) => {
      if (this.constructor.NON_RECURRING_ADDONS.includes(key)) {
        once[key] = quantity;
      } else {
        recurring[key] = quantity;
      }
    });

    return {recurring, once};
  }

  async refreshPricingWithAddons() {
    const requestId = this.nextRequest();

    await this.refreshAllPlanPrices(requestId);
  }
}
